import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { COLORS } from '../../../../utils/colors';
import { AppButton } from '../../../general/AppButton/AppButton';
import emptyListImg from '../../../../assets/images/emptyList.png';
import { Eye, Edit3 } from 'react-feather';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import moment from 'moment';
import axios from 'axios';
import { PDFDocument } from 'pdf-lib';
import { twMerge } from 'tailwind-merge';
import {
  CaretLeft,
  CaretRight,
  ArrowsDownUp,
  SortDescending,
  SortAscending,
  Trash,
  UploadSimple,
  Truck,
} from 'phosphor-react';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';
import { FiledLienApiResponse } from '@dill/dill-shared';
import ReactPaginate from 'react-paginate';
import {
  downloadFile,
  disablePDFFields,
  flattenPDF,
  buildSupplierPath,
} from '../../../../utils/helpers';
import { getSupplierFiledLiens } from '../../../../redux/services/filedLiensService';
import { AppTrackPreliminaryNoticeModal } from '../../../general/AppTrackPreliminaryNoticeModal/AppTrackPreliminaryNoticeModal';
import { AppImagePreviewModal } from '../../../general/AppImagePreviewModal/AppImagePreviewModal';
import AppCustomDropdown from '../../../general/AppCustomDropdown/AppCustomDropdown';

const FiledLiensTable = ({
  searchedFiledLiens,
  refresh,
}: {
  searchedFiledLiens: FiledLienApiResponse[];
  refresh: boolean;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { filedLiens } = useAppSelector((state) => state.filedLiens);
  const [showPreviewPdf, setShowPreviewPdf] = useState(false);
  const [showPreviewImage, setShowPreviewImage] = useState(false);
  const [showTrackNoticeModal, setShowTrackNoticeModal] = useState(false);
  const [selectedFiledLien, setSelectedFiledLien] = useState<FiledLienApiResponse | null>(null);
  const [openDropdown, setOpenDropDown] = useState('');

  const [sort, setSort] = useState('');
  const [sorted, setSorted] = useState(false);
  const [sortedFiledLiens, setSortedFiledLiens] = useState<FiledLienApiResponse[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [previewAttachment, setPreviewAttachment] = useState(false);

  // Pagination
  const [itemOffset, setItemOffset] = useState(0);
  const itemsPerPage = 20; // Number of items per page
  const pageCount = Math.ceil(searchedFiledLiens.length / itemsPerPage);
  const endOffset = itemOffset + itemsPerPage;
  const currentItems = sortedFiledLiens.slice(itemOffset, endOffset);
  const currentDate = new Date();
  const endOfMonthCurrent = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const startOfMonthThreeMonthsAgo = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 2,
    1
  );
  const utcOffset = moment().utcOffset();
  const hours = Math.floor(utcOffset / 60);

  const handlePageClick = (event: any) => {
    setCurrentPage(event.selected + 1);
    const newOffset = (event.selected * itemsPerPage) % searchedFiledLiens.length;
    setItemOffset(newOffset);
  };
  const cummulativeOffset = useMemo(() => {
    let result = 0;
    const currentTotalItems = endOffset / itemsPerPage;
    if (pageCount === currentTotalItems) {
      result = searchedFiledLiens.length;
    } else if (searchedFiledLiens.length === 0) {
      result = 0;
    } else {
      const itemsTotal = currentTotalItems * itemsPerPage;
      result = itemsTotal;
    }
    return result;
  }, [endOffset, searchedFiledLiens]);

  useEffect(() => {
    setSortedFiledLiens(searchedFiledLiens);
    setItemOffset(0);
    setCurrentPage(1);
  }, [searchedFiledLiens]);

  useEffect(() => {
    if (user) {
      dispatch(getSupplierFiledLiens({ supplierId: currentSupplier?.id || '' }));
    }

    return () => {};
  }, [refresh, user?.id]);

  const fetchPdf = async (url: string) => {
    const res = await axios.get(url, {
      responseType: 'arraybuffer',
    });
    if (res.data) {
      return res.data;
    }
    return null;
  };

  const columns = useMemo(() => {
    return [
      { id: 'submittedOn', name: 'Submitted Date', width: 120, cellType: 'VIEW_TEXT' },
      { id: 'job', name: 'Job', width: 150, cellType: 'VIEW_TEXT' },
      { id: 'customer', name: 'Customer', width: 150, cellType: 'VIEW_TEXT' },
      { id: 'gc', name: 'GC', width: 150, cellType: 'VIEW_TEXT' },
      { id: 'owner', name: 'Owner', width: 150, cellType: 'VIEW_TEXT' },
      { id: 'lender', name: 'Lender', width: 150, cellType: 'VIEW_TEXT' },
      { id: 'status', name: 'Status', width: 100, cellType: 'VIEW_TEXT' },
      { id: 'actions', name: '', width: 170, cellType: 'VIEW_TEXT' },
    ];
  }, []);
  const totalWidth = useMemo(
    () =>
      columns.reduce((curr, prev) => {
        return curr + prev.width;
      }, 0),
    [columns]
  );
  const handleDownloadSelectedPdf = async (filedLien: FiledLienApiResponse) => {
    if (filedLien?.pdf?.url) {
      const flattenedPDF = await flattenPDF(filedLien?.pdf?.url);
      if (flattenedPDF) {
        const createdAtTimeStamp = filedLien?.createdAt
          ? new Date(filedLien.createdAt).getTime()
          : new Date().getTime();
        downloadFile(
          flattenedPDF || '',
          `${filedLien.job?.name ?? ''}-${
            filedLien.job?.buyer?.name ?? ''
          }-${createdAtTimeStamp}.pdf`
        );
      }
    }
  };
  const handlePreviewPdf = async (filedLien: FiledLienApiResponse) => {
    setShowPreviewPdf(true);
  };

  const sortByProperty = (
    sortType: 'asc' | 'desc',
    sortKey: string,
    getter: (item: any) => any,
    compare?: (a: any, b: any) => number
  ) => {
    const filteredReleases = searchedFiledLiens.filter((filedLien) => {
      const value = getter(filedLien);
      return value !== undefined && value !== null;
    });

    const sortedReleasesCopy = [...filteredReleases];
    sortedReleasesCopy.sort((a, b) => {
      const valueA = getter(a);
      const valueB = getter(b);

      if (compare) {
        return compare(valueA, valueB) * (sortType === 'asc' ? 1 : -1);
      } else {
        const stringA = String(valueA).toUpperCase();
        const stringB = String(valueB).toUpperCase();

        if (stringA < stringB) {
          return sortType === 'asc' ? -1 : 1;
        } else if (stringA > stringB) {
          return sortType === 'asc' ? 1 : -1;
        }

        return 0;
      }
    });

    const undefinedOrNullReleases = searchedFiledLiens.filter((filedLien) => {
      const value = getter(filedLien);
      return value === undefined || value === null;
    });

    const sortedReleases = sortedReleasesCopy.concat(undefinedOrNullReleases);

    setSortedFiledLiens(sortedReleases);
  };

  useEffect(() => {
    if (location.pathname.startsWith(buildSupplierPath(currentSupplier?.id, '/liens/overview'))) {
      const params = new URLSearchParams(location.search);
      const filedLienId = params.get('lienId');
      if (filedLienId) {
        const filedLien = searchedFiledLiens.find((prelien) => prelien.id === filedLienId);
        if (filedLien) {
          setSelectedFiledLien(filedLien);
          handlePreviewPdf(filedLien);
        }
      }
    }
  }, [location.pathname, searchedFiledLiens]);

  const handleSort = (value: string) => {
    if (searchedFiledLiens) {
      if (sort === value) {
        if (sorted) {
          setSortedFiledLiens(searchedFiledLiens);
          setSort('');
          setSorted(false);
        } else {
          if (value === 'submittedOn') {
            sortByProperty('desc', value, (filedLien) => filedLien.createdAt);
          } else if (value === 'customer') {
            sortByProperty('desc', value, (filedLien) => filedLien.job?.buyer?.name);
          } else if (value === 'owner') {
            sortByProperty('desc', value, (filedLien) => filedLien?.job?.owners?.[0]?.name ?? '');
          } else if (value === 'lender') {
            sortByProperty('desc', value, (filedLien) => filedLien?.job?.lenders?.[0]?.name ?? '');
          } else if (value === 'job') {
            sortByProperty(
              'desc',
              value,
              (filedLien) => filedLien.job?.jobNumber ?? 0,
              (a, b) => a - b
            );
          } else if (value === 'gc') {
            sortByProperty('desc', value, (filedLien) => filedLien.job?.GCs?.[0]?.name ?? '');
          }

          setSorted(true);
        }
      } else {
        if (value === 'submittedOn') {
          sortByProperty('asc', value, (filedLien) => filedLien.createdAt);
        } else if (value === 'customer') {
          sortByProperty('asc', value, (filedLien) => filedLien.job?.buyer?.name);
        } else if (value === 'owner') {
          sortByProperty('asc', value, (filedLien) => filedLien?.job?.owners?.[0]?.name ?? '');
        } else if (value === 'lender') {
          sortByProperty('asc', value, (filedLien) => filedLien?.job?.lenders?.[0]?.name ?? '');
        } else if (value === 'job') {
          sortByProperty(
            'asc',
            value,
            (filedLien) => filedLien.job?.jobNumber ?? 0,
            (a, b) => a - b
          );
        } else if (value === 'gc') {
          sortByProperty('asc', value, (filedLien) => filedLien.job?.GCs?.[0]?.name ?? '');
        }

        setSort(value);
        setSorted(false);
      }
    }
  };

  const handlePreviewAttachmentPDF = (url: string, fileType?: string) => {
    if (fileType && fileType !== 'pdf') {
      setAttachmentUrl(url);
      setShowPreviewImage(true);
    } else {
      setAttachmentUrl(url);
      setPreviewAttachment(true);
      setShowPreviewPdf(true);
    }
  };

  const handleEditFiledLien = (filedLienId: string) => {
    navigate(buildSupplierPath(currentSupplier?.id, `/liens/overview/editLien/${filedLienId}`));
  };

  return (
    <div className="w-full h-[calc(100vh-190px)] flex flex-col">
      <div className="w-full h-full flex flex-col border rounded-lg  overflow-y-scroll mt-4">
        <AppTrackPreliminaryNoticeModal
          open={showTrackNoticeModal}
          handleClose={() => {
            setShowTrackNoticeModal(false);
            setPreviewAttachment(false);
          }}
          preliminaryNotice={selectedFiledLien}
          onPreviewAttachment={handlePreviewAttachmentPDF}
        />
        <AppImagePreviewModal
          open={showPreviewImage}
          handleClose={() => {
            setShowPreviewImage(false);
          }}
          title="Return Attachment"
          url={attachmentUrl}
        />
        <AppPdfPreviewModal
          open={showPreviewPdf}
          handleClose={() => {
            setShowPreviewPdf(false);
            setAttachmentUrl('');
            if (
              location.pathname.startsWith(
                buildSupplierPath(currentSupplier?.id, '/liens/overview')
              )
            ) {
              const params = new URLSearchParams(location.search);
              const filedLienId = params.get('lienId');
              if (filedLienId) {
                navigate(buildSupplierPath(currentSupplier?.id, '/liens/overview'));
              }
            }
          }}
          invoice={''}
          pdfType="PRELIMINARY_NOTICE"
          pdfList={[
            {
              name: previewAttachment ? 'Return Attachment' : 'Lien',
              url: previewAttachment ? attachmentUrl : selectedFiledLien?.pdf?.url || '',
              date: new Date(),
            },
          ]}
          handleDownloadPdf={() => {
            if (selectedFiledLien) {
              handleDownloadSelectedPdf(selectedFiledLien);
            }
          }}
          isSigned={false}
          mode="Supplier"
        />
        <div className="w-full flex border-b justify-between px-4 sticky top-0 PRIMARY_50-BG items-center z-10">
          {columns.map((column, i) => {
            return (
              <div
                key={column.id}
                style={{ flex: column.width / totalWidth }}
                className={twMerge('flex py-3 mx-1', 'items-center')}>
                {column.id !== 'status' && column.id !== 'actions' ? (
                  <div onClick={() => handleSort(column.id)} className="cursor-pointer flex">
                    <p className="text-xs TEXT_SECONDARY-CLR mr-2">{column.name}</p>
                    <div>
                      {sort === column.id && sorted ? (
                        <SortAscending color={COLORS.GREY_400} />
                      ) : sort === column.id && !sorted ? (
                        <SortDescending color={COLORS.GREY_400} />
                      ) : (
                        <ArrowsDownUp color={COLORS.GREY_400} />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="min-w-[150px]">
                    <p className="text-xs TEXT_SECONDARY-CLR items-center">{column.name}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {searchedFiledLiens.length === 0 && (
          <div className="w-full h-[70vh] flex items-center justify-center">
            <div className="flex flex-col items-center">
              <img className="w-3/6 object-contain" src={emptyListImg} alt="" />
              <p>No Liens</p>
            </div>
          </div>
        )}
        {currentItems.length > 0 &&
          currentItems.map((filedLien, index) => {
            const checkMailedStatus =
              (filedLien?.lendersStatus.length > 0
                ? filedLien?.lendersStatus
                    ?.filter((status) => status?.isNeeded !== false)
                    ?.every((lenderStatus) => lenderStatus?.mailedDate != null)
                : true) &&
              (filedLien?.ownersStatus.length > 0
                ? filedLien?.ownersStatus
                    ?.filter((status) => status?.isNeeded !== false)
                    ?.every((ownerStatus) => ownerStatus?.mailedDate != null)
                : true) &&
              (filedLien?.contractorsStatus.length > 0
                ? filedLien?.contractorsStatus
                    ?.filter((status) => status?.isNeeded !== false)
                    ?.every((contractorStatus) => contractorStatus?.mailedDate != null)
                : true) &&
              (filedLien?.customersStatus && filedLien?.customersStatus?.length > 0
                ? filedLien?.customersStatus
                    ?.filter((status) => status?.isNeeded !== false)
                    ?.every((customerStatus) => customerStatus?.mailedDate != null)
                : true) &&
              (filedLien?.suretiesStatus && filedLien?.suretiesStatus?.length > 0
                ? filedLien?.suretiesStatus
                    ?.filter((status) => status?.isNeeded !== false)
                    ?.every((suretyStatus) => suretyStatus?.mailedDate != null)
                : true);
            const status =
              filedLien?.status === 'completed'
                ? 'Complete'
                : checkMailedStatus
                ? 'Complete'
                : 'Pending';
            return (
              <div
                key={index + 'oo'}
                className="w-full flex items-center  px-4 justify-between border-b border-b-gray-200">
                {columns.map((column, i) => {
                  return (
                    <div
                      key={column.id}
                      style={{ flex: column.width / totalWidth }}
                      className={`flex py-3 mx-1 ${
                        columns.length - 1 !== i ? 'overflow-hidden' : ''
                      }`}>
                      {column.id === 'submittedOn' && (
                        <p className="text-sm capitalize w-full">
                          {filedLien?.createdAt
                            ? moment(filedLien?.createdAt).format('MM/DD/YYYY')
                            : ''}
                        </p>
                      )}
                      {column.id === 'customer' && (
                        <div className="flex  w-full">
                          <div className="">
                            <div className="flex">
                              <p className="text-sm capitalize ">
                                {filedLien?.job?.buyer?.name.toLocaleLowerCase() || ''}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {column.id === 'gc' && (
                        <p className="text-sm capitalize w-full">
                          {filedLien?.job?.GCs?.[0]?.name}
                        </p>
                      )}
                      {column.id === 'owner' && (
                        <p className="text-sm capitalize w-full">
                          {filedLien?.job?.owners?.[0]?.name || ''}
                        </p>
                      )}
                      {column.id === 'lender' && (
                        <p className="text-sm capitalize w-full">
                          {filedLien?.job?.lenders?.[0]?.name || 'N/A'}
                        </p>
                      )}
                      {column.id === 'job' && (
                        <div className="w-full flex flex-col">
                          <p className="text-xs capitalize font-bold">
                            {filedLien.job?.jobNumber || ''}
                          </p>
                          <p className="text-xs capitalize ">{filedLien.job?.name || ''}</p>
                        </div>
                      )}
                      {column.id === 'status' && (
                        <div className="w-full">
                          <p className="text-xs capitalize ">{status}</p>
                        </div>
                      )}
                      {column.id === 'actions' && (
                        <div>
                          <div className="flex min-w-[150px] gap-1.5">
                            {filedLien.templateId === '' && (
                              <div>
                                <div className="w-[35px] h-4"></div>
                              </div>
                            )}
                            {!checkMailedStatus && filedLien?.status !== 'completed' ? (
                              <div className="flex">
                                <AppCustomDropdown
                                  inputFieldStyles={{ width: '35px' }}
                                  onSelect={(val) => {
                                    setSelectedFiledLien(filedLien);
                                    if (val === 'edit') {
                                      handleEditFiledLien(filedLien.id);
                                    }
                                  }}
                                  options={[
                                    {
                                      value: 'edit',
                                      label: 'Edit',
                                      icon: <Edit3 size={16} />,
                                    },
                                  ]}
                                  open={openDropdown === filedLien.id}
                                  onOpen={() => setOpenDropDown(filedLien.id)}
                                  onClose={() => setOpenDropDown('')}
                                  width="100px"
                                />
                              </div>
                            ) : (
                              <div>
                                <div className="w-[35px] h-4"></div>
                              </div>
                            )}
                            <AppButton
                              type="TERTIARY"
                              buttonStyles={{
                                padding: '8px',
                                height: '35px',
                                width: '35px',
                              }}
                              icon={<Eye color={COLORS.GREY_500} size={20} />}
                              onClick={() => {
                                setSelectedFiledLien(filedLien);
                                handlePreviewPdf(filedLien);
                                navigate(
                                  buildSupplierPath(
                                    currentSupplier?.id,
                                    `/liens/overview?lienId=${filedLien?.id}`
                                  )
                                );
                              }}
                            />
                            {filedLien.templateId !== '' && (
                              <AppButton
                                type="TERTIARY"
                                buttonStyles={{
                                  padding: '8px',
                                  height: '35px',
                                  width: '35px',
                                }}
                                icon={<Truck color={COLORS.GREY_500} size={20} />}
                                onClick={() => {
                                  setSelectedFiledLien(filedLien);
                                  setShowTrackNoticeModal(true);
                                }}
                              />
                            )}
                            <AppButton
                              type="PRIMARY"
                              text="Download"
                              buttonStyles={{
                                height: '35px',
                              }}
                              onClick={() => {
                                handleDownloadSelectedPdf(filedLien);
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
      </div>
      {/* Pagination component */}
      <div className="flex flex-row justify-between items-center w-3/5 mt-3">
        <div className="text-xs GREY_500-CLR">{`Showing ${
          searchedFiledLiens.length !== 0 ? `${itemOffset + 1} to` : ''
        } ${cummulativeOffset} from ${searchedFiledLiens.length} Lien${
          searchedFiledLiens.length > 1 ? 's' : ''
        }`}</div>
        <ReactPaginate
          breakLabel="..."
          onPageChange={handlePageClick}
          pageRangeDisplayed={3}
          pageCount={pageCount}
          forcePage={Math.min(Math.max(0, currentPage - 1), pageCount - 1)}
          previousLabel={<CaretLeft size={20} />}
          nextLabel={<CaretRight size={20} />}
          renderOnZeroPageCount={null}
          containerClassName="pagination"
          activeClassName="active"
        />
      </div>
    </div>
  );
};

export default FiledLiensTable;
